import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { Subject, takeUntil } from 'rxjs';
import { ReturnBatchesService } from './return-batches.service';
import { ReturnBatchItem, ReturnDetail } from './return-batch.types';
import { loadLabelCodeLibraries, packageQrImage, renderLabelBarcode } from '../products-preview/package-label-codes';

@Component({
  selector: 'app-return-batch-labels', standalone: true, imports: [CommonModule, FormsModule, DialogModule],
  templateUrl: './return-batch-labels.component.html',
  styleUrls: ['../products-preview/product-create-dialog.component.scss', '../../assets/print/product-package-label-layout.css', './return-batch-labels.component.scss']
})
export class ReturnBatchLabelsComponent implements OnInit, OnDestroy {
  @ViewChild('printArea') printArea?: ElementRef<HTMLElement>;
  @ViewChildren('modelBarcode') modelBarcodes!: QueryList<ElementRef<SVGElement>>;
  @ViewChildren('serialBarcode') serialBarcodes!: QueryList<ElementRef<SVGElement>>;
  detail: ReturnDetail | null = null;
  items: (ReturnBatchItem & { qr: string })[] = [];
  loading = true; printing = false; codesReady = false; error = '';
  brand: 'FBI' | 'ULAAP' = 'FBI';
  private alive = true;
  private readonly destroyed = new Subject<void>();
  constructor(@Inject(DIALOG_DATA) public data: { uuid: string; ids: string[]; theme: 'dark' | 'light' },
    public readonly ref: DialogRef, private readonly service: ReturnBatchesService, private readonly changes: ChangeDetectorRef) {}
  get logo(): string { return this.brand === 'FBI' ? 'assets/fbi_logo_side_split_difference_small.png' : 'assets/ulaap_logo.png'; }
  get condition(): string { return ({ 1: 'New', 2: 'Used', 3: 'Damaged', 4: 'Open Box' } as Record<number, string>)[this.detail?.batch.condition || 2]; }
  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading = true; this.codesReady = false; this.error = ''; this.items = []; this.detail = null;
    this.service.detail(this.data.uuid).pipe(takeUntil(this.destroyed)).subscribe({
      next: async (detail) => {
        try {
          if (detail.batch.product_return_uuid !== this.data.uuid) { throw new Error('Invalid batch identity.'); }
          const selected = detail.items.filter((i) => this.data.ids.includes(i.product_uuid));
          if (selected.length !== new Set(this.data.ids).size || selected.some((i) => i.label_available !== true
            || !/^[a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(i.package_uuid))) {
            throw new Error('Some product packaging changed. Refresh the batch and select its printable products again.');
          }
          const renderer = await loadLabelCodeLibraries();
          const items = await Promise.all(selected.map(async (i) => ({ ...i, qr: await packageQrImage('package,' + i.package_uuid) })));
          if (!this.alive) { return; }
          this.detail = detail; this.items = items; this.changes.detectChanges();
          items.forEach((item, index) => {
            renderLabelBarcode(renderer, this.modelBarcodes.get(index)?.nativeElement, item.model_number);
            renderLabelBarcode(renderer, this.serialBarcodes.get(index)?.nativeElement, item.serial_number || item.package_id);
          });
          this.codesReady = true;
        } catch (e) { if (this.alive) { this.error = e instanceof Error ? e.message : 'Labels could not render.'; } }
        finally { if (this.alive) { this.loading = false; this.changes.detectChanges(); } }
      }, error: (e) => { this.loading = false; this.error = e.error?.message || 'Labels could not load.'; }
    });
  }
  async print(): Promise<void> {
    if (!this.printArea || !this.codesReady || this.printing || this.error) { return; }
    const page = window.open('', '_blank', 'width=520,height=780');
    if (!page) { this.error = 'Allow pop-ups for DAMS, then retry printing.'; return; }
    this.printing = true;
    try {
      const css = page.document.createElement('link'); css.rel = 'stylesheet';
      css.href = new URL('assets/print/return-batch-labels.css', document.baseURI).href;
      const stylesReady = new Promise<void>((resolve, reject) => {
        css.onload = () => resolve(); css.onerror = () => reject(new Error('Print stylesheet could not load.'));
      });
      page.document.head.appendChild(css);
      page.document.title = 'Return labels ' + this.detail!.batch.return_number;
      const labels = this.printArea.nativeElement.cloneNode(true) as HTMLElement;
      labels.querySelectorAll('img').forEach((img) => { img.src = new URL(img.getAttribute('src') || '', document.baseURI).href; });
      page.document.body.appendChild(labels);
      await Promise.all([stylesReady, ...Array.from(labels.querySelectorAll('img')).map((img) => img.decode())]);
      await page.document.fonts.ready;
      page.focus(); page.print();
    } catch {
      this.error = 'The print preview could not finish loading. Retry after the label images are visible.'; page.close();
    } finally { this.printing = false; }
  }
  ngOnDestroy(): void { this.alive = false; this.destroyed.next(); this.destroyed.complete(); }
}
